import React, { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Calendar, List, Map, Filter, Search, Plus, Download, ChevronLeft, ChevronRight, Save as SaveIcon, MapPin } from 'lucide-react'
import EventCard from '../components/ui/EventCard'
import FilterPanel from '../components/ui/FilterPanel'
import { Event } from '../types'
import { getBrandForHost } from '../branding/brandConfig'
import { Link } from 'react-router-dom'

const CalendarPage = () => {
  const [view, setView] = useState<'list' | 'calendar' | 'map'>('list')
  const [filters, setFilters] = useState<any>({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [savedSearches, setSavedSearches] = useState<{ name: string, filters: any }[]>([])
  const [query, setQuery] = useState<string>('')

  const mockEvents: Event[] = [
    {
      id: '1',
      title: 'Summer Music Festival',
      description: 'A celebration of local and regional talent featuring multiple stages.',
      artist: 'Multiple Artists',
      venue: 'Century II',
      startsAt: '2025-07-15T18:00:00Z',
      price: '$45',
      genres: ['Festival', 'Rock', 'Pop'],
      image: 'https://images.pexels.com/photos/1190297/pexels-photo-1190297.jpeg?auto=compress&cs=tinysrgb&w=800',
      attendees: 234,
      rating: 4.8,
      reviews: 56,
      featured: true,
      status: 'upcoming',
      location: {
        address: '225 W Douglas Ave, Wichita, KS 67202',
        coordinates: [37.6872, -97.3301]
      }
    },
    {
      id: '2',
      title: 'Acoustic Evening with Sarah Mitchell',
      description: 'An intimate acoustic performance featuring original songs and covers.',
      artist: 'Sarah Mitchell',
      venue: 'The Cotillion',
      startsAt: '2025-07-18T20:00:00Z',
      price: '$25',
      genres: ['Acoustic', 'Folk', 'Indie'],
      image: 'https://images.pexels.com/photos/1763075/pexels-photo-1763075.jpeg?auto=compress&cs=tinysrgb&w=800',
      attendees: 89,
      rating: 4.9,
      reviews: 23,
      status: 'upcoming',
      location: {
        address: '856 S Main St, Wichita, KS 67213',
        coordinates: [37.6620, -97.3364]
      }
    },
    {
      id: '3',
      title: 'Rock the Night',
      description: 'High-energy rock performance that will get you moving.',
      artist: 'Thunder Road',
      venue: 'Lava Tonic',
      startsAt: '2025-07-20T21:00:00Z',
      price: '$30',
      genres: ['Rock', 'Classic Rock'],
      image: 'https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&w=800',
      attendees: 156,
      rating: 4.7,
      reviews: 34,
      status: 'upcoming',
      location: {
        address: '1447 S Clifton Ave, Wichita, KS 67218',
        coordinates: [37.6456, -97.2951]
      }
    }
  ]

  const brand = getBrandForHost(window.location.hostname)
  const exportICS = () => {
    const lines: string[] = []
    lines.push('BEGIN:VCALENDAR')
    lines.push('VERSION:2.0')
    lines.push(`PRODID:-//${brand.siteName.replace(/\s+/g, '')}//Calendar//EN`)
    for (const ev of mockEvents) {
      const dt = new Date(ev.startsAt)
      const dtUTC = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
      const stamp = dtUTC.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
      lines.push('BEGIN:VEVENT')
      lines.push(`UID:site-${ev.id}@${new URL(brand.baseUrl).hostname}`)
      lines.push(`DTSTAMP:${stamp}`)
      lines.push(`DTSTART:${stamp}`)
      lines.push(`SUMMARY:${ev.title}`)
      lines.push(`LOCATION:${ev.venue}`)
      lines.push(`DESCRIPTION:${ev.description}`)
      lines.push('END:VEVENT')
    }
    lines.push('END:VCALENDAR')
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const file = brand.siteName.toLowerCase().replace(/\s+/g, '-') + '.ics'
    a.download = file
    a.click()
    URL.revokeObjectURL(url)
  }

  // Imported events (admin CSV stub via localStorage)
  const importedEvents: Event[] = React.useMemo(() => {
    try {
      const raw = localStorage.getItem('importedEvents')
      if (!raw) return []
      const arr = JSON.parse(raw)
      return Array.isArray(arr) ? arr : []
    } catch { return [] }
  }, [])

  const [fetchedEvents, setFetchedEvents] = React.useState<Event[]>([])
  React.useEffect(() => {
    fetch('/data/events.json')
      .then((r) => r.json())
      .then((d) => Array.isArray(d) ? setFetchedEvents(d) : void 0)
      .catch(() => {})
  }, [])

  const allEvents = React.useMemo(() => [...fetchedEvents, ...mockEvents, ...importedEvents], [fetchedEvents, importedEvents])

  // Saved searches
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('calendarSavedSearches')
      if (raw) setSavedSearches(JSON.parse(raw))
    } catch {}
  }, [])

  const persistSaved = (next: { name: string, filters: any }[]) => {
    setSavedSearches(next)
    localStorage.setItem('calendarSavedSearches', JSON.stringify(next))
  }

  const saveSearch = () => {
    const name = window.prompt('Name this search')
    if (!name) return
    const merged = { ...filters, ...(query ? { query } : {}) }
    const next = [...savedSearches.filter(s => s.name !== name), { name, filters: merged }]
    persistSaved(next)
  }

  const applySaved = (s: { name: string, filters: any }) => {
    setFilters(s.filters || {})
    setQuery(s.filters?.query || '')
  }
  const deleteSaved = (name: string) => persistSaved(savedSearches.filter(s => s.name !== name))

  const quickFilters = [
    'All Events',
    'Tonight',
    'This Weekend',
    'Free',
    'Rock',
    'Jazz',
    'Electronic'
  ]

  const applyQuickFilter = (label: string) => {
    if (label === 'All Events') { setFilters({}); return }
    if (label === 'Tonight') { setFilters({ date: 'tonight' }); return }
    if (label === 'This Weekend') { setFilters({ date: 'weekend' }); return }
    if (label === 'Free') { setFilters({ price: 'free' }); return }
    setFilters({ genre: label.toLowerCase() })
  }

  return (
    <div className="pt-16 lg:pt-20 min-h-screen">
      {/* Header */}
      <section className="section-padding py-12 bg-gradient-to-b from-le-darker/50 to-transparent">
        <div className="container-max">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-6xl font-display font-bold text-white mb-4">
              Event
              <span className="text-gradient block">Calendar</span>
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Never miss a show. Discover all the live music happening in Wichita.
            </p>
          </motion.div>

          {/* Search and Filters */}
          <motion.div
            className="glass-card p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  placeholder="Search events, artists, venues..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-le-accent transition-colors duration-200"
                />
              </div>

              {/* View Toggle */}
              <div className="flex bg-white/10 rounded-xl p-1">
                {[
                  { key: 'list', icon: List, label: 'List' },
                  { key: 'calendar', icon: Calendar, label: 'Calendar' },
                  { key: 'map', icon: Map, label: 'Map' }
                ].map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => setView(key as any)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                      view === key
                        ? 'bg-le-accent text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button 
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Filter size={18} />
                  <span>Filters</span>
                  {Object.keys(filters).length > 0 && (
                    <span className="bg-le-accent text-white text-xs px-2 py-1 rounded-full">
                      {Object.keys(filters).length}
                    </span>
                  )}
                </button>
                <button className="btn-secondary flex items-center space-x-2" onClick={exportICS}>
                  <Download size={18} />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button className="btn-secondary flex items-center space-x-2" onClick={saveSearch}>
                  <SaveIcon size={18} />
                  <span className="hidden sm:inline">Save</span>
                </button>
                <button className="btn-primary flex items-center space-x-2">
                  <Plus size={18} />
                  <span>Add Event</span>
                </button>
              </div>
            </div>

            {/* Saved Searches + Filter Tags */}
            <div className="flex flex-wrap gap-2 mt-4">
              {savedSearches.map((s) => (
                <span key={s.name} className="flex items-center gap-2 bg-white/10 text-white/80 text-sm px-3 py-1 rounded-full">
                  <button className="underline" onClick={() => applySaved(s)}>{s.name}</button>
                  <button className="text-white/50 hover:text-white" onClick={() => deleteSaved(s.name)} title="Delete">×</button>
                </span>
              ))}
              {quickFilters.slice(0, 6).map((filter) => (
                <button
                  key={filter}
                  onClick={() => applyQuickFilter(filter)}
                  className="px-3 py-1 bg-white/10 hover:bg-le-accent/20 text-white/70 hover:text-white rounded-full text-sm transition-all duration-200"
                >
                  {filter}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pb-20">
          <div className="container-max flex gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {(() => {
              const visibleEvents = allEvents.filter((ev) => {
                if (query) {
                  const q = query.toLowerCase()
                  const hay = `${ev.title} ${ev.artist} ${ev.venue}`.toLowerCase()
                  if (!hay.includes(q)) return false
                }
                if (filters.genre) {
                  const g = String(filters.genre).toLowerCase()
                  if (!ev.genres.some((x) => x.toLowerCase().includes(g))) return false
                }
                if (filters.price === 'free') {
                  const p = (ev.price || '').toLowerCase()
                  if (!(p.includes('free') || p.includes('$0'))) return false
                }
                if (filters.date === 'tonight') {
                  const now = new Date()
                  const d = new Date(ev.startsAt)
                  if (d.toDateString() !== now.toDateString()) return false
                }
                if (filters.date === 'weekend') {
                  const d = new Date(ev.startsAt)
                  const day = d.getDay()
                  if (!(day === 0 || day === 6)) return false
                }
                return true
              })

              return (
                <>
                  {view === 'list' && (
                    <motion.div className="space-y-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                      {visibleEvents.map((event, index) => (
                        <motion.div key={event.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
                          <Link to={`/events/${event.id}`} className="block">
                            <EventCard event={event} size="large" />
                          </Link>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {view === 'calendar' && (
                    <motion.div className="glass-card p-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                      {/* Calendar header and grid remain */}
                    </motion.div>
                  )}

                  {view === 'map' && (
                    <motion.div className="glass-card p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
                      <div className="relative aspect-[16/10] w-full rounded-xl overflow-hidden bg-black/30">
                        <div className="absolute inset-0">
                          {visibleEvents.filter(e => Array.isArray(e.location?.coordinates)).map((ev, i) => {
                            const [lat, lng] = ev.location.coordinates
                            const x = ((lng + 180) / 360) * 100
                            const y = ((90 - lat) / 180) * 100
                            return (
                              <div key={`${ev.id}-${i}`} className="absolute" style={{ left: `${x}%`, top: `${y}%` }} title={`${ev.title} @ ${ev.venue}`}>
                                <div className="-translate-x-1/2 -translate-y-full text-le-accent">
                                  <MapPin size={20} />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                      <p className="text-white/60 text-xs mt-2">Markers are based on current filters and search.</p>
                    </motion.div>
                  )}
                </>
              )
            })()}

            {view === 'calendar' && (
              <motion.div
                className="glass-card p-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-display font-semibold text-white">
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))}
                      className="p-2 text-white/60 hover:text-white transition-colors duration-200"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={() => setCurrentDate(new Date())}
                      className="px-4 py-2 bg-white/10 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))}
                      className="p-2 text-white/60 hover:text-white transition-colors duration-200"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="p-4 text-center text-white/60 font-medium">
                      {day}
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: 35 }, (_, i) => {
                    const dayNumber = i - 5 // Adjust based on month start
                    const hasEvent = dayNumber > 0 && dayNumber <= 31 && Math.random() > 0.7
                    
                    return (
                      <div
                        key={i}
                        className="min-h-[120px] p-2 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors duration-200 cursor-pointer"
                      >
                        {dayNumber > 0 && dayNumber <= 31 && (
                          <>
                            <div className="text-white/70 text-sm mb-2">{dayNumber}</div>
                            {hasEvent && (
                              <div className="bg-le-accent/20 text-le-accent text-xs px-2 py-1 rounded mb-1">
                                Event
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              </motion.div>
            )}

            {view === 'map' && (
              <motion.div
                className="glass-card p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative aspect-[16/10] w-full rounded-xl overflow-hidden bg-black/30">
                  {/* Simple pseudo-map: render markers relative to basic projection */}
                  <div className="absolute inset-0">
                    {allEvents.filter(e => Array.isArray(e.location?.coordinates)).map((ev, i) => {
                      const [lat, lng] = ev.location.coordinates
                      // Rough projection to container (not accurate, placeholder)
                      const x = ((lng + 180) / 360) * 100
                      const y = ((90 - lat) / 180) * 100
                      return (
                        <div key={`${ev.id}-${i}`} className="absolute" style={{ left: `${x}%`, top: `${y}%` }} title={`${ev.title} @ ${ev.venue}`}>
                          <div className="-translate-x-1/2 -translate-y-full text-le-accent">
                            <MapPin size={20} />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <p className="text-white/60 text-xs mt-2">Markers use a simple projection for demo; we will switch to an interactive map SDK later.</p>
              </motion.div>
            )}
          </div>

          {/* Filter Sidebar */}
          <div className="hidden lg:block w-80">
            <FilterPanel
              isOpen={true}
              onClose={() => {}}
              filters={filters}
              onFiltersChange={setFilters}
              type="events"
            />
          </div>
        </div>
      </section>

      {/* Mobile Filter Panel */}
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={filters}
        onFiltersChange={setFilters}
        type="events"
      />
    </div>
  )
}

export default CalendarPage